//#region Imports & External Dependencies
import { FieldType, KeyValuePair } from "@shoothill/core";
import { ViewModelBase } from "@shoothill/core";
import { makeObservable, observable, computed, action } from "mobx";
import { container } from "tsyringe";
import { AppUrls } from "AppUrls";
import { LookupStore } from "Stores/Domain/LookupStore";
import { Mentor, SessionDocument, StudentSessionContext, StudentSessionContextValidator } from "Shared/Models/StudentSessionReport";
import { AccountStore } from "Stores/Domain/AccountStores";
import { WeeklyRotaManagersModule } from "./Managers/WeeklyRotaManagersModule";
//#endregion

//#region Class/Component Definition
export class WeeklyRotaSessionReportViewModel extends ViewModelBase<StudentSessionContext> {
    //#region Properties & State
    public accountStore = container.resolve(AccountStore);
    public lookupStore = container.resolve(LookupStore);
    public studentId: string = "";
    public reportId: string = "";
    public showError: boolean = false;
    public managers!: WeeklyRotaManagersModule;
    //#endregion

    //#region Constructor/Initialization
    constructor(id: string | undefined, mentorId: string | undefined, reportId: string | undefined, studentId: string | undefined) {
        super(new StudentSessionContext());
        this.model.id = id ?? null;
        this.model.mentorId = mentorId ?? "";
        this.model.studentId = studentId ?? "";
        this.studentId = studentId ?? "";
        this.reportId = reportId ?? "";
        this.setValidator(new StudentSessionContextValidator());
        this.managers = new WeeklyRotaManagersModule(this);
        this.model.filesViewModel = this.managers.mobx.filesViewModel;

        makeObservable(this, {
            studentId: observable,
            showError: observable,
            setFormErrorMessage: action,
            showErrorsCommand: action,
            mentorWithoutMatchingEmail: computed,
        });
    }
    //#endregion

    //#region Lifecycle Methods
    public initialize = async () => {
        if (this.managers.viewMode.isEditMode) {
            await this.managers.api.loadSessionReportById(this.reportId);
            this.managers.mobx.setUpsertButtonDisplayName("Update");
        } else {
            await this.managers.api.loadNewStudentSessionReportContext(this.model.studentId!);
            this.managers.mobx.setUpsertButtonDisplayName("Create");
        }
        this.managers.domain.initializeTargetViewModels();
    };
    //#endregion

    //#region Delegated to managers
    public get apiClient() {
        return this.managers.api.apiClient;
    }
    public get isUpserting() {
        return this.managers.mobx.isUpserting;
    }
    public get upsertButtonDisplayName() {
        return this.managers.mobx.upsertButtonDisplayName;
    }
    public get studentAbsenceViewModel() {
        return this.managers.mobx.studentAbsenceViewModel;
    }
    public get filesViewModel() {
        return this.managers.mobx.filesViewModel;
    }
    public get studentTargetViewModels() {
        return this.managers.mobx.studentTargetViewModels;
    }
    public get studentBehaviourViewModels() {
        return this.managers.mobx.studentBehaviourViewModels;
    }
    public get studentSessionLegacyFormViewModel() {
        return this.managers.mobx.studentSessionLegacyFormViewModel;
    }
    public get studentSessionAQACertificatesViewModel() {
        return this.managers.mobx.studentSessionAQACertificatesViewModel;
    }
    public get viewMode() {
        return this.managers.viewMode;
    }

    public setStudentTargetViewModels(viewModels: import("Views/Shared/Views/StudentTarget/StudentTargetViewModel").StudentTargetViewModel[]) {
        this.managers.mobx.setStudentTargetViewModels(viewModels);
    }
    public setStudentBehaviourViewModels(viewModels: import("Views/Shared/Views/StudentBehaviour/StudentBehaviourViewModel").StudentBehaviourViewModel[]) {
        this.managers.mobx.setStudentBehaviourViewModels(viewModels);
    }
    public setSessionAQACertificatesViewModel(
        vm: import("Views/Shared/Views/StudentSessionAQACertificate/StudentSessionAQACertificatesViewModel").StudentSessionAQACertificatesViewModel,
    ) {
        this.managers.mobx.setSessionAQACertificatesViewModel(vm);
    }
    public setLegacyFormViewModel(vm: import("Views/Shared/Views/StudentSessionLegacyForm/StudentSessionLegacyFormViewModel").StudentSessionLegacyFormViewModel) {
        this.managers.mobx.setLegacyFormViewModel(vm);
    }
    public resetFilesViewModel() {
        this.managers.mobx.resetFilesViewModel();
        this.model.filesViewModel = this.managers.mobx.filesViewModel;
    }
    public canSubmitForm() {
        return this.managers.domain.canSubmitForm();
    }
    //#endregion

    //#region Commands (delegated to managers where shared)
    public get navigateToBackCommand() {
        return this.managers.commands.navigateToBackCommand;
    }
    public get confirmNavigateToStudentSessionsListCommand() {
        return this.managers.commands.confirmNavigateToStudentSessionsListCommand;
    }
    public get dismissCancelUpdateWarningCommand() {
        return this.managers.commands.dismissCancelUpdateWarningCommand;
    }
    public get updateSessionDateCommand() {
        return this.managers.commands.updateSessionDateCommand;
    }
    public get updateSessionStartTimeCommand() {
        return this.managers.commands.updateSessionStartTimeCommand;
    }
    public get updateSessionEndTimeCommand() {
        return this.managers.commands.updateSessionEndTimeCommand;
    }
    public get updateSessionStatusCommand() {
        return this.managers.commands.updateSessionStatusCommand;
    }
    public get updateAbsenceReasonCommand() {
        return this.managers.commands.updateAbsenceReasonCommand;
    }
    public get updateAbsenceReasonDetailCommand() {
        return this.managers.commands.updateAbsenceReasonDetailCommand;
    }
    public get updateActivityTypeCommand() {
        return this.managers.commands.updateActivityTypeCommand;
    }
    public get updateWellbeingCommand() {
        return this.managers.commands.updateWellbeingCommand;
    }
    public get updateInvolvementCommand() {
        return this.managers.commands.updateInvolvementCommand;
    }
    public get checkSelectStudentTargetCommand() {
        return this.managers.commands.checkSelectStudentTargetCommand;
    }
    public get updateDocumentCategoryCommand() {
        return this.managers.commands.updateDocumentCategoryCommand;
    }
    public get updateReportIncidentCommand() {
        return this.managers.commands.updateReportIncidentCommand;
    }
    public get updateSessionPlanCommand() {
        return this.managers.commands.updateSessionPlanCommand;
    }
    public get updateSessionFeedbackCommand() {
        return this.managers.commands.updateSessionFeedbackCommand;
    }
    public get updateMentorCommand() {
        return this.managers.commands.updateMentorCommand;
    }
    public get uploadStudentReportCommand() {
        return this.managers.commands.uploadStudentReportCommand;
    }
    public get removeStudentDocumentsCommand() {
        return this.managers.commands.removeStudentDocumentsCommand;
    }
    public get downloadResourceDocumentCommand() {
        return this.managers.commands.downloadResourceDocumentCommand;
    }
    public get showSessionDocPhotoCommand() {
        return this.managers.commands.showSessionDocPhotoCommand;
    }
    public get upsertStudentSessionCommand() {
        return this.managers.commands.upsertStudentSessionCommand;
    }
    public get dismissFormHasErrorsWarningCommand() {
        return this.managers.commands.dismissFormHasErrorsWarningCommand;
    }

    public get cancelCommand() {
        return this.managers.commands.cancelCommand;
    }
    //#endregion

    //#region WeeklyRota-specific
    public setFormErrorMessage(value: string | null) {
        this.managers.mobx.setFormErrorMessage(value);
    }

    public showErrorsCommand = () => {
        this.showError = !this.showError;
    };

    public get mentorWithoutMatchingEmail(): boolean {
        return this.model.sessionMentor.email !== this.accountStore.DisplayName;
    }
    //#endregion

    //#region Display Getters (delegate to domain / mobx)
    public get formErrorMessage() {
        return this.managers.mobx.formErrorMessage;
    }
    public get showFormErrorWarning() {
        return this.managers.mobx.formErrorMessage != null;
    }
    public get showCancelUpdateWarningMessage() {
        return this.managers.mobx.showCancelUpdateWarningMessage;
    }
    public get cancelUpdateWarningMessage() {
        return this.managers.mobx.cancelUpdateWarningMessage;
    }
    public get canSubmitSessionDoc() {
        return this.managers.mobx.canSubmitSessionDoc;
    }
    public get displayInvolvement() {
        return this.model.wellbeing.find((item) => item.id === this.model.involvementId);
    }
    public get displayStatus() {
        return this.managers.domain.displayStatus;
    }
    public get displayWellbeing() {
        return this.model.wellbeing.find((item) => item.id === this.model.wellbeingId);
    }
    public get getActivity() {
        return this.managers.domain.activity;
    }
    public get getAttendance() {
        return this.managers.domain.attendanceOptions;
    }
    public get getDocumentCategory() {
        return this.managers.domain.documentCategories;
    }
    public get getReason() {
        return this.managers.domain.absenceReasons;
    }
    public get getSessionStatus() {
        return this.managers.domain.sessionStatuses;
    }
    public get getsessionMentorName() {
        return this.managers.domain.mentorKeyValuePairs.find((m) => m.key === this.model.mentorId)?.text;
    }
    public get renderForm() {
        return this.managers.domain.renderForm;
    }
    public get renderGetAttendance() {
        const studentAttendance = this.getAttendance.filter((m) => m.key === this.model.attendance);
        const attended = this.getAttendance.filter((m) => m.key === "1");
        return studentAttendance.length > 0 ? studentAttendance : attended;
    }
    public get showExport() {
        return true;
    }
    public get showUploadButton() {
        this.model.filesViewModel = this.managers.mobx.filesViewModel;
        return false;
    }
    public get mentorKeyValuePairs() {
        return this.managers.domain.mentorKeyValuePairs;
    }
    public get showInvolvementError() {
        return this.getError("involvementId") != undefined;
    }
    public get showWellbeingError() {
        return this.getError("wellbeingId") != undefined;
    }
    public get showSessionDocPhoto() {
        return this.managers.mobx.showSessionDocPhoto;
    }
    public get availableDocuments() {
        return this.managers.domain.filteredSessionDocuments;
    }
    public get sessionDocuments() {
        return this.managers.domain.filteredSessionDocuments;
    }
    public dismissUploadModal(): void {
        this.managers.mobx.setShowSessionDocPhoto(false);
        this.setValue("documentCategoryId", "");
        this.setValid("documentCategoryId", false);
    }
    public get showMentorSelect(): boolean {
        return false;
    }
    public get backPath(): string {
        return AppUrls.Client.Admin.Mentor.WeeklyRota;
    }
    public get showAbsenceFields(): boolean {
        return false;
    }
    public get attendanceOptions() {
        return this.renderGetAttendance;
    }
    public get getAbsenceReasons() {
        return this.getReason;
    }
    public get showOtherAbsenceText() {
        return this.managers.mobx.showOtherAbsenceText;
    }
    public get showStudentBehaviours() {
        return this.managers.domain.showStudentBehaviours;
    }
    public get exportReportCommand() {
        return undefined;
    }
    public get isExporting() {
        return false;
    }
    public get exportButtonDisplayName() {
        return "Export";
    }
    public get emotionalRegulationMentoringProgramme() {
        return this.model.student.emotionalRegulationMentoringProgramme ?? "";
    }

    public getSessionStatusById(id: string) {
        return this.managers.domain.getSessionStatusById(id);
    }
    public getSessionActivityType(id: string) {
        if (this.getActivity.length > 0) {
            const item = this.getActivity.find((m) => m.key === id);
            return item ? item.text : "";
        }
        return "";
    }
    public getReasonType(id: string) {
        if (this.getReason.length > 0) {
            const item = this.getReason.find((m) => m.key === id);
            return item ? item.text : "";
        }
        return "";
    }
    public getDocumentCategoryName(id: string) {
        return this.managers.domain.getDocumentCategoryName(id);
    }
    //#endregion
}
//#endregion
